import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Globe,
  ArrowRight,
  Github,
  Twitter,
  Linkedin,
  Youtube,
  ChevronDown,
} from "lucide-react";
import Footer from "./Footer";

// --- DATA: COMPREHENSIVE COUNTRY LIST ---
const countryList = [
  { name: "Australia", dial: "+61" },
  { name: "India (भारत)", dial: "+91" },
  { name: "New Zealand", dial: "+64" },
  { name: "Qatar", dial: "+974" },
  { name: "Saudi Arabia ", dial: "+966" },
  { name: "Sri Lanka  ", dial: "+94" },
  { name: "Singapore  ", dial: "+65" },
  { name: "UAE  ", dial: "+971" },
  { name: "United Kingdom", dial: "+44" },
  { name: "United States", dial: "+1" },
].sort((a, b) => a.name.localeCompare(b.name));

const serviceOptions = [
  "Security Consultation",
  "Implementation Service",
  "Security Awareness Training",
  "Governance, Risk & Compliance",
  "Penetration Testing",
  "Managed Security Services",
  "Managed Detection and Response",
  "24/7 Security Operations (SOC)",
  "Other",
];

const officeData = [
  {
    region: "Bangalore, India",
    timezone: "Asia/Kolkata",
    address: "Opulence, Level 1, Jayanagar, Bangalore, Karnataka, India",
    phone: "+91",
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1769434894/Bangalore_xehsvv.png",
    directions:
      "https://www.google.com/maps/search/?api=1&query=Opulence+Level+1+Jayanagar+Bangalore+Karnataka+India",
  },
  {
    region: "Hyderabad, India",
    timezone: "Asia/Kolkata",
    address: "Level 2, Bison Enclave, Yapral, Hyderabad, Telangana, India",
    phone: "+91",
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1769434887/Hyderabad_cjlon5.png",
    directions:
      "https://www.google.com/maps/search/?api=1&query=Bison+Enclave+Yapral+Hyderabad+Telangana+India",
  },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    businessEmail: "",
    country: "",
    phoneCountry: "India (भारत)",
    phoneDial: "+91",
    phone: "",
    companyName: "",
    jobTitle: "",
    helpWith: "",
    message: "",
    agreed: false, // For Privacy Policy requirement
  });

  const [captcha, setCaptcha] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [officeTimes, setOfficeTimes] = useState({});
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Captcha Generator
  const generateCaptcha = () => {
    const chars =
      "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(result);
  };

  useEffect(() => {
    generateCaptcha();
    const updateTimes = () => {
      const times = {};
      officeData.forEach((office) => {
        times[office.region] = new Intl.DateTimeFormat("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: office.timezone,
        }).format(new Date());
      });
      setOfficeTimes(times);
    };
    updateTimes();
    const interval = setInterval(updateTimes, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "phoneCountry") {
      const selected = countryList.find((c) => c.name === value);
      setFormData((prev) => ({
        ...prev,
        phoneCountry: value,
        phoneDial: selected ? selected.dial : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verify Captcha matches
    if (captchaInput !== captcha) {
      alert("Captcha does not match. Please try again.");
      generateCaptcha();
      setCaptchaInput("");
      return;
    }

    setSubmitting(true);
    setStatus("");

    try {
      await fetch(
        // "https://script.google.com/macros/s/AKfycbziIm1hHjtqbnX5wq2QkqJwwC8isvdWbYefexgbaCA32CgxMWKZoRyUklq8HJctBLl8/exec",
        "https://script.google.com/macros/s/AKfycbzbcUGFOeM2Ykvtun0_K4eSeRdYeg01ZDq3Yn7rgZoYCJqqFW_X507XPJsu5y4xDGVmqA/exec",
        {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify(formData),
        },
      );

      setStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        businessEmail: "",
        country: "",
        phoneCountry: "India (भारत)",
        phoneDial: "+91",
        phone: "",
        companyName: "",
        jobTitle: "",
        helpWith: "",
        message: "",
        agreed: false,
      });
      setCaptchaInput("");
      generateCaptcha();
    } catch (error) {
      console.error("Submission Error:", error);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      <section className="bg-[#032d28] pt-24 pb-44 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-[42px] font-light text-[#169a8a] mb-6">
            Contact <span className="text-[#169a8a] font-normal">Us</span>
          </h1>
          <p className="text-gray-300 text-[15px] max-w-2xl mx-auto leading-relaxed">
            Do you have a question or need more information? Please complete the
            form below and a representative from Synthera Security will follow
            up.
          </p>
        </div>
      </section>

      <section className="px-6 -mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto bg-white p-10 md:p-14 shadow-xl rounded-sm border-t-4 border-[#169a8a]"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <InputField
                label="First name"
                name="firstName"
                value={formData.firstName}
                required
                onChange={handleChange}
              />
              <InputField
                label="Last name"
                name="lastName"
                value={formData.lastName}
                required
                onChange={handleChange}
              />
              <InputField
                label="Company email"
                name="businessEmail"
                type="email"
                value={formData.businessEmail}
                required
                onChange={handleChange}
              />
              <InputField
                label="Country"
                name="country"
                isSelect
                options={countryList.map((c) => c.name)}
                value={formData.country}
                required
                onChange={handleChange}
              />

              <div className="flex flex-col">
                <label className="text-[13px] font-medium mb-2 text-gray-600">
                  Mobile Phone*
                </label>
                <div className="flex gap-2">
                  <div className="relative border border-gray-300 p-2 text-sm w-44 bg-white flex items-center justify-between cursor-pointer">
                    <select
                      name="phoneCountry"
                      value={formData.phoneCountry}
                      onChange={handleChange}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full"
                    >
                      {countryList.map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <span className="truncate">{formData.phoneCountry}</span>
                    <ChevronDown size={14} className="text-gray-400" />
                  </div>
                  <div className="flex-1 flex border border-gray-300 overflow-hidden">
                    <span className="p-2 text-sm text-gray-500 bg-gray-50 border-r border-gray-300 min-w-[50px] text-center">
                      {formData.phoneDial}
                    </span>
                    <input
                      type="text"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                      className="flex-1 p-2 text-sm outline-none"
                    />
                  </div>
                </div>
              </div>

              <InputField
                label="Company name"
                name="companyName"
                value={formData.companyName}
                required
                onChange={handleChange}
              />
              <InputField
                label="Job Title"
                name="jobTitle"
                value={formData.jobTitle}
                required
                onChange={handleChange}
              />
              <InputField
                label="What can we help you with?"
                name="helpWith"
                isSelect
                options={serviceOptions}
                value={formData.helpWith}
                required
                onChange={handleChange}
              />
            </div>

            <div className="w-full">
              <label className="block text-[13px] font-medium mb-2 text-gray-600">
                Message*
              </label>
              <textarea
                name="message"
                required
                value={formData.message}
                rows="4"
                className="w-full border border-gray-300 p-3 text-sm outline-none focus:border-green-600"
                onChange={handleChange}
              ></textarea>
            </div>

            {/* CAPTCHA SECTION - Added like UnderAttack page */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center bg-gray-50 p-4 rounded-sm border border-gray-200">
              <div className="flex flex-col">
                <label className="text-[13px] font-medium mb-2 text-gray-600">
                  Verify Captcha*
                </label>
                <input
                  type="text"
                  placeholder="Enter characters below"
                  required
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  className="border border-gray-300 p-3 text-sm outline-none focus:border-green-600 bg-white"
                />
              </div>
              <div className="flex items-center justify-center gap-4 bg-white border border-dashed border-gray-400 p-3">
                <span className="font-mono text-2xl tracking-[0.5em] select-none text-[#032d28] font-bold">
                  {captcha}
                </span>
                <button
                  type="button"
                  onClick={generateCaptcha}
                  className="text-green-600 hover:rotate-180 transition-transform duration-500"
                >
                  <ArrowRight size={20} className="rotate-90" />
                </button>
              </div>
            </div>

            {/* PRIVACY POLICY - Mandatory for Submit */}
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreed"
                  required
                  checked={formData.agreed}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 accent-[#032d28]"
                />
                <span className="text-[12px] text-gray-500 leading-tight">
                  Yes, I agree to receive periodic emails from Synthera
                  Security.{" "}
                  {/* <a
                    // href="https://drive.google.com/file/d/1TCiXzwyL3Dof8x2E7GZOl_1G_SnPswLn/view?usp=sharing"
                    className="text-blue-500 underline"
                  >
                    Privacy Policy
                  </a>
                  . */}
                </span>
              </label>
            </div>

            <div className="text-center pt-6">
              {status === "success" && (
                <p className="text-green-600 font-bold mb-4">
                  Message sent successfully!
                </p>
              )}
              {status === "error" && (
                <p className="text-red-600 font-bold mb-4">
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || !formData.agreed}
                className="bg-[#012f28] text-white px-28 py-3 font-bold uppercase tracking-widest text-[13px] hover:bg-[#1c4843] transition-all shadow-md disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Submit"}
              </button>
              {!formData.agreed && (
                <p className="text-[10px] text-gray-400 mt-2 italic">
                  Please agree to the privacy policy to enable submit.
                </p>
              )}
            </div>
          </form>
        </motion.div>
      </section>

      {/* --- OFFICE BANNER & CARDS --- */}
      <section className="bg-[#032d28] mt-24 py-16 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="flex flex-col gap-6">
            <h2 className="text-white text-[42px] font-light">
              Our <span className="font-bold">Offices</span>
            </h2>

            <div className="flex items-center gap-4 bg-white px-6 py-3 rounded-sm">
              <span className="text-gray-800 text-[12px] font-bold uppercase">
                Follow Us
              </span>

              <div className="flex gap-4 text-gray-400">
                {/* GitHub */}
                <a
                  href="https://github.com/syntherasecurity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Github
                    size={18}
                    className="hover:text-black cursor-pointer"
                  />
                </a>

                {/* Twitter */}
                <a
                  href="https://x.com/SyntheraSec"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Twitter
                    size={18}
                    className="hover:text-blue-400 cursor-pointer"
                  />
                </a>

                {/* LinkedIn */}
                <a
                  href="//www.linkedin.com/company/syntherasecurity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin
                    size={18}
                    className="hover:text-blue-700 cursor-pointer"
                  />
                </a>

                {/* YouTube */}
                <a
                  href="https://youtube.com/@yourchannel"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Youtube
                    size={18}
                    className="hover:text-red-600 cursor-pointer"
                  />
                </a>
              </div>
            </div>
          </div>

          <div className="text-white/30 transform rotate-12 hidden lg:block">
            <Globe size={110} strokeWidth={0.5} />
          </div>
        </div>
      </section>

      <section className="bg-white py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 shadow-2xl">
            {officeData.map((office, idx) => (
              <div
                key={idx}
                className="group flex flex-col border-r border-gray-100 last:border-0 overflow-hidden"
              >
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={office.image}
                    alt={office.region}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <p className="text-[11px] font-bold mb-1">
                      {officeTimes[office.region] || "Loading..."}
                    </p>
                    <h3 className="text-[26px] font-bold">{office.region}</h3>
                  </div>
                </div>
                <div className="bg-[#012f28] p-10 text-white flex-grow">
                  <p className="text-[12px] text-gray-300 font-light mb-6">
                    {office.address}
                  </p>
                  {/* <p className="text-[12px] font-bold">
                    Phone:{" "}
                    <span className="font-normal text-gray-300 ml-1">
                      {office.phone}
                    </span>
                  </p> */}
                  <a
                    href={office.directions}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#00a1e4] text-[11px] font-bold uppercase mt-10 hover:text-white transition-colors no-underline"
                  >
                    Get Directions
                    <ArrowRight
                      size={14}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Internal Sub-component
const InputField = ({
  label,
  name,
  value,
  type = "text",
  required,
  isSelect,
  options,
  onChange,
}) => (
  <div className="flex flex-col">
    <label className="text-[13px] font-medium mb-2 text-gray-600">
      {label}
      {required && "*"}
    </label>
    {isSelect ? (
      <div className="relative">
        <select
          name={name}
          value={value}
          required={required}
          onChange={onChange}
          className="w-full border border-gray-300 p-2 text-sm outline-none appearance-none bg-white focus:border-green-600"
        >
          <option value="">Select {label}</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="absolute right-3 top-3 text-gray-400 pointer-events-none"
        />
      </div>
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        required={required}
        onChange={onChange}
        className="border border-gray-300 p-2 text-sm outline-none focus:border-green-600"
      />
    )}
  </div>
);

export default Contact;
